import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useCookies } from 'react-cookie';
import MultiLanguageContext from '.';
import useProjectIdContext from '../../Contexts/ProjectContexts/ProjectIdContext/useProjectIdContext';
import useProjectInfoContext from '../../Contexts/ProjectContexts/ProjectInfoContext/useProjectInfoContext';

export default function MultiLanguageContextProvider({ children }: { children: React.ReactNode }) {
  const { projectId } = useProjectIdContext();
  const { project } = useProjectInfoContext();
  const [cookies, setCookies] = useCookies();
  const queryClient = useQueryClient();

  const setLanguage = React.useCallback((language: string) => {
    if (projectId) {
      setCookies(`current-project.${projectId}.language`, language, {
        path: '/',
        sameSite: 'none',
        secure: true,
        maxAge: 365 * 3600 * 24,
      });
      queryClient.invalidateQueries(['listLiveProjectCards', { input: { id: projectId } }]);
      queryClient.invalidateQueries(['listLiveProjects']);
      queryClient.invalidateQueries(['searchCards', { input: { id: projectId } }]);
      queryClient.invalidateQueries(['searchCards.infinite', { input: { id: projectId } }]);
      queryClient.invalidateQueries(['preloadCards', { input: { id: projectId } }]);
      queryClient.invalidateQueries(['listLatestUpdateCards', { input: { id: projectId } }]);
      queryClient.invalidateQueries(['getProjectCompliance', { input: { id: projectId } }]);
      queryClient.invalidateQueries(['listCommands', { input: { id: projectId } }]);
      queryClient.invalidateQueries(['getLiveCard']);
      queryClient.invalidateQueries(['getCard']);
      queryClient.invalidateQueries(['listDecisionTrees']);
      queryClient.invalidateQueries(['getLiveProjectCommandContexts', { input: { id: projectId } }]);
    }
  }, [projectId, setCookies]);

  const language = React.useMemo(() => cookies[`current-project.${projectId}.language`], [cookies, projectId]);

  const languageRef = React.useRef(language);
  React.useEffect(() => {
    languageRef.current = language;
  }, [language]);

  const projectLanguageOptions = React.useMemo(() => [
    { original: true, language: project?.asrLanguage, id: project?.asrLanguage },
    ...(project?.translations ?? []).map((translation) => ({ original: false, id: translation.language, language: translation.language })),
  ], [project, language]);

  const value = React.useMemo(() => ({
    setLanguage,
    language: projectLanguageOptions.find((l) => l.language === language)?.language ?? project?.asrLanguage,
    projectLanguageOptions,
  }), [cookies, projectId, setLanguage, language, projectLanguageOptions]);

  return (
    <MultiLanguageContext.Provider value={value}>
      {children}
    </MultiLanguageContext.Provider>
  );
}
